(() => {
  'use strict';
  const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  function initRail(rail) {
    if (rail.dataset.bxReady) return;
    rail.dataset.bxReady = 'true';
    const arrows = rail.parentElement.querySelector('[data-bx-arrows]');
    const prev = arrows?.querySelector('[data-bx-prev]');
    const next = arrows?.querySelector('[data-bx-next]');
    const move = (direction) => {
      const card = rail.firstElementChild;
      if (!card) return;
      const distance = card.getBoundingClientRect().width + parseFloat(getComputedStyle(rail).columnGap || 0);
      rail.scrollBy({ left: distance * direction, behavior: reduceMotion() ? 'auto' : 'smooth' });
    };
    const sync = () => {
      if (!arrows) return;
      arrows.hidden = rail.scrollWidth <= rail.clientWidth + 3;
      prev.disabled = rail.scrollLeft <= 3;
      next.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 4;
    };
    prev?.addEventListener('click', () => move(-1));
    next?.addEventListener('click', () => move(1));
    rail.addEventListener('keydown', event => {
      if (event.target !== rail) return;
      if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') move(event.key === 'ArrowLeft' ? -1 : 1);
        else rail.scrollTo({left:event.key === 'Home' ? 0 : rail.scrollWidth,behavior:reduceMotion()?'auto':'smooth'});
      }
    });
    rail.addEventListener('scroll', sync, {passive:true});
    new ResizeObserver(sync).observe(rail);
    sync();
  }
  function initUGC(root) {
    if (root.dataset.bxReady) return;
    root.dataset.bxReady = 'true';
    const videos = Array.from(root.querySelectorAll('[data-bx-video]'));
    const label = video => video.closest('[data-bx-sound]').querySelector('[data-bx-sound-label]');
    const paintSound = video => {
      const button = video.closest('[data-bx-sound]');
      const on = !video.muted;
      const text = on ? 'Sound on' : 'Tap for sound';
      button.setAttribute('aria-pressed', String(on));
      // the accessible name starts with the words on screen (WCAG 2.5.3)
      button.setAttribute('aria-label', `${text}: ${video.getAttribute('aria-label')}`);
      label(video).textContent = text;
      const card = button.closest('.bx-video-card');
      if (card) card.classList.toggle('is-sound', on);
      root.classList.toggle('has-sound', videos.some(v => !v.muted));
    };
    const muteAll = except => videos.forEach(video => { if (video !== except) {video.muted = true; paintSound(video);} });
    // Owner (28 Sep 2026): "ugc video should be continuously played". Every visitor sees the clips play on their own,
    // muted and looping, including with Reduce Motion or Data Saver on. Clips on screen play together; clips off screen
    // pause and stop downloading, and carry on when they are back on screen. Where the browser still blocks autoplay
    // (for example an iPhone in Low Power Mode), the first tap or key press anywhere on the page starts them.
    const onScreen = new Set();
    const unlockEvents = ['touchend', 'click', 'keydown'];
    let unlockArmed = false;
    root.classList.add('bx-ugc--js');
    function unlock() {
      unlockEvents.forEach(type => document.removeEventListener(type, unlock, true));
      unlockArmed = false;
      onScreen.forEach(video => { if (video.paused) playMuted(video); });
    }
    function playMuted(video) {
      video.loop = true;
      video.playsInline = true;
      const attempt = video.play();
      if (attempt && attempt.catch) attempt.catch(() => {
        if (!video.paused) return;
        label(video).textContent = 'Tap to watch with sound';
        if (!unlockArmed) {
          unlockArmed = true;
          unlockEvents.forEach(type => document.addEventListener(type, unlock, true));
        }
      });
    }
    videos.forEach(video => {
      video.defaultMuted = true;
      video.muted = true;
      paintSound(video);
      video.addEventListener('volumechange', () => paintSound(video));
      video.addEventListener('playing', () => { video.classList.add('is-live'); paintSound(video); });
      video.closest('[data-bx-sound]').addEventListener('click', () => {
        const turnOn = video.muted || video.paused;
        muteAll(video);
        video.muted = !turnOn;
        paintSound(video);
        // play() stays in the trusted click event for browsers that block autoplay; turning sound off keeps it playing.
        if (turnOn) video.play().catch(() => { video.muted = true; paintSound(video); });
      });
      video.addEventListener('error', () => {
        label(video).textContent = 'Video unavailable';
      });
    });
    if ('IntersectionObserver' in window) {
      const watcher = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const video = entry.target;
          if (entry.isIntersecting) {
            onScreen.add(video);
            playMuted(video);
          } else {
            onScreen.delete(video);
            if (!video.muted) { video.muted = true; paintSound(video); }
            video.pause();
          }
        });
      }, {threshold: 0.3});
      videos.forEach(video => watcher.observe(video));
    } else videos.forEach(playMuted);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) muteAll();
      else onScreen.forEach(playMuted);
    });
    const form = root.querySelector('[data-bx-quick-form]');
    const button = root.querySelector('[data-bx-quick-add]');
    const status = root.querySelector('[data-bx-cart-status]');
    form?.addEventListener('submit', event => {
      if (!document.querySelector('.bao-hero__form')) return;
      event.preventDefault();
      status.textContent = '';
      document.dispatchEvent(new CustomEvent('bao:quick-add', {detail:{button}}));
    });
    document.addEventListener('bao:quick-add-result', event => {
      status.textContent = event.detail.ok ? 'BAO Refresh added to your cart.' : event.detail.message;
    });
    document.addEventListener('bao:variant:change', event => {
      const variant = event.detail?.variant;
      if (!variant) return;
      form.querySelector('[name=id]').value = variant.id;
      button.disabled = !variant.available;
      button.textContent = variant.available ? 'Add BAO to cart' : 'Sold out';
      const heroPrice = document.querySelector('[data-bao-hero-price-now]');
      if (heroPrice) root.querySelector('[data-bx-price]').textContent = heroPrice.textContent;
    });
  }
  function boot(scope=document) {
    scope.querySelectorAll('[data-bx-rail]').forEach(initRail);
    scope.querySelectorAll('[data-bx-ugc]').forEach(initUGC);
  }
  boot();
  document.addEventListener('shopify:section:load', event => boot(event.target));
})();
